import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Users, Trash2, Plus, Link2Off, Search } from 'lucide-react';
import { api } from '../lib/api';
import type { Role } from '@shared/types';

interface Permission {
  id: string;
  userId: string;
  scopeType: 'section' | 'chapter';
  scopeId: string;
  canRead: number;
  canEdit: number;
  breaksInheritance: number;
  grantedAt: number;
}

interface User {
  id: string;
  email: string;
  name: string;
  role: Role;
  isActive: number;
}

interface Props {
  scopeType: 'section' | 'chapter';
  scopeId: string;
  /** Pro chapter skrýváme `breaks_inheritance` toggle na sekci — platí jen pro kapitoly. */
  showBreakInheritance?: boolean;
}

export default function PermissionsPanel({ scopeType, scopeId, showBreakInheritance }: Props) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);

  const permsQuery = useQuery({
    queryKey: ['permissions', scopeType, scopeId],
    queryFn: () =>
      api.get<{ permissions: Permission[] }>(
        `/permissions?scope_type=${scopeType}&scope_id=${scopeId}`,
      ),
  });

  const usersQuery = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => api.get<{ users: User[] }>('/admin/users'),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['permissions', scopeType, scopeId] });

  const upsert = useMutation({
    mutationFn: (body: {
      user_id: string;
      can_read: boolean;
      can_edit: boolean;
      breaks_inheritance?: boolean;
    }) => api.post('/permissions', { scope_type: scopeType, scope_id: scopeId, ...body }),
    onSuccess: invalidate,
  });

  const bulkUpsert = useMutation({
    mutationFn: async (body: {
      user_ids: string[];
      can_read: boolean;
      can_edit: boolean;
      reader_ids: Set<string>;
    }) => {
      // Pro Readery vynutíme can_edit=false (nedávalo by smysl). API vrací 200/201 per user.
      await Promise.all(
        body.user_ids.map((id) =>
          api.post('/permissions', {
            scope_type: scopeType,
            scope_id: scopeId,
            user_id: id,
            can_read: body.can_read || body.can_edit, // edit implicitně vyžaduje read
            can_edit: body.reader_ids.has(id) ? false : body.can_edit,
          }),
        ),
      );
    },
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/permissions/${id}`),
    onSuccess: invalidate,
  });

  const perms = permsQuery.data?.permissions ?? [];
  const allUsers = (usersQuery.data?.users ?? []).filter((u) => u.isActive === 1);
  const byId = new Map(allUsers.map((u) => [u.id, u] as const));
  const existingUserIds = new Set(perms.map((p) => p.userId));
  const candidateUsers = allUsers.filter((u) => !existingUserIds.has(u.id) && u.role !== 'admin');

  return (
    <div className="card mt-10 p-6">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-teal" />
          <h3 className="font-heading text-lg font-bold text-dark">Oprávnění</h3>
          <span className="text-xs text-gray-500">
            Adminové mají automaticky plný přístup. Níže spravuj explicitní přístupy pro Editory a Readery.
          </span>
        </div>
        {!adding && candidateUsers.length > 0 && (
          <button onClick={() => setAdding(true)} className="btn-ghost">
            <Plus className="h-4 w-4" /> Přidat uživatele
          </button>
        )}
      </div>

      {adding && (
        <AddUsersPanel
          candidates={candidateUsers}
          onCancel={() => setAdding(false)}
          onSubmit={(body) => {
            bulkUpsert.mutate(body, { onSuccess: () => setAdding(false) });
          }}
          isPending={bulkUpsert.isPending}
        />
      )}

      {permsQuery.isLoading ? (
        <div className="text-sm text-gray-500">Načítám…</div>
      ) : perms.length === 0 && !adding ? (
        <div className="rounded-md bg-gray-50 p-4 text-center text-sm text-gray-500">
          Žádná explicitní oprávnění. {scopeType === 'chapter' && 'Kapitola dědí přístup od rodiče.'}
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
              <th className="py-2 text-left">Uživatel</th>
              <th className="py-2 text-center">Číst</th>
              <th className="py-2 text-center">Editovat</th>
              {showBreakInheritance && <th className="py-2 text-center">Přerušit dědičnost</th>}
              <th className="py-2"></th>
            </tr>
          </thead>
          <tbody>
            {perms.map((p) => {
              const u = byId.get(p.userId);
              return (
                <tr key={p.id} className="border-b border-gray-100 last:border-0">
                  <td className="py-2">
                    <div className="font-semibold text-dark">{u?.name ?? '(neznámý)'}</div>
                    <div className="text-xs text-gray-500">{u?.email}</div>
                  </td>
                  <td className="py-2 text-center">
                    <Toggle
                      value={p.canRead === 1}
                      onChange={(v) =>
                        upsert.mutate({
                          user_id: p.userId,
                          can_read: v,
                          can_edit: p.canEdit === 1,
                          breaks_inheritance: p.breaksInheritance === 1,
                        })
                      }
                    />
                  </td>
                  <td className="py-2 text-center">
                    <Toggle
                      value={p.canEdit === 1}
                      disabled={u?.role === 'reader'}
                      title={u?.role === 'reader' ? 'Reader nemůže mít edit-právo. Povyš ho nejdřív na Editor.' : undefined}
                      onChange={(v) =>
                        upsert.mutate({
                          user_id: p.userId,
                          can_read: p.canRead === 1 || v, // edit implicitně vyžaduje read
                          can_edit: v,
                          breaks_inheritance: p.breaksInheritance === 1,
                        })
                      }
                    />
                  </td>
                  {showBreakInheritance && (
                    <td className="py-2 text-center">
                      <button
                        type="button"
                        onClick={() =>
                          upsert.mutate({
                            user_id: p.userId,
                            can_read: p.canRead === 1,
                            can_edit: p.canEdit === 1,
                            breaks_inheritance: p.breaksInheritance !== 1,
                          })
                        }
                        title={
                          p.breaksInheritance === 1
                            ? 'Dědičnost přerušena — nadřazená sekce se pro tohoto usera ignoruje.'
                            : 'Přerušit dědičnost z nadřazené sekce.'
                        }
                        className={
                          p.breaksInheritance === 1
                            ? 'inline-flex items-center gap-1 text-xs font-semibold text-gold'
                            : 'inline-flex items-center gap-1 text-xs text-gray-400 hover:text-gold'
                        }
                      >
                        <Link2Off className="h-3.5 w-3.5" />
                        {p.breaksInheritance === 1 ? 'Ano' : 'Ne'}
                      </button>
                    </td>
                  )}
                  <td className="py-2 text-right">
                    <button
                      onClick={() => {
                        if (confirm(`Odebrat oprávnění pro ${u?.name ?? u?.email}?`)) remove.mutate(p.id);
                      }}
                      className="rounded-md p-1.5 text-gray-400 hover:bg-danger/10 hover:text-danger"
                      title="Odebrat oprávnění"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function AddUsersPanel({
  candidates,
  onCancel,
  onSubmit,
  isPending,
}: {
  candidates: User[];
  onCancel: () => void;
  onSubmit: (body: {
    user_ids: string[];
    can_read: boolean;
    can_edit: boolean;
    reader_ids: Set<string>;
  }) => void;
  isPending: boolean;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [canRead, setCanRead] = useState(true);
  const [canEdit, setCanEdit] = useState(false);
  const [filter, setFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState<'all' | Role>('all');

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return candidates.filter((c) => {
      if (roleFilter !== 'all' && c.role !== roleFilter) return false;
      if (!q) return true;
      return c.name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q);
    });
  }, [candidates, filter, roleFilter]);

  const allFilteredIds = useMemo(() => filtered.map((c) => c.id), [filtered]);
  const allFilteredSelected =
    allFilteredIds.length > 0 && allFilteredIds.every((id) => selected.has(id));

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllFiltered = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) allFilteredIds.forEach((id) => next.delete(id));
      else allFilteredIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const readerIds = useMemo(
    () => new Set(candidates.filter((c) => c.role === 'reader').map((c) => c.id)),
    [candidates],
  );

  const selectedArr = Array.from(selected);
  const selectedReaderCount = selectedArr.filter((id) => readerIds.has(id)).length;

  return (
    <div className="mb-4 rounded-md border border-teal/30 bg-teal-light/50 p-4">
      {/* Filter + select all */}
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filtrovat podle jména nebo e-mailu…"
            className="input pl-8"
          />
        </div>
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value as 'all' | Role)}
          className="input w-auto"
        >
          <option value="all">Všechny role</option>
          <option value="editor">Editor</option>
          <option value="reader">Reader</option>
        </select>
        <button type="button" onClick={toggleAllFiltered} className="btn-ghost whitespace-nowrap">
          {allFilteredSelected ? 'Odznačit vše' : `Označit všechny (${allFilteredIds.length})`}
        </button>
      </div>

      {/* Scrollable user list */}
      <div className="mb-3 max-h-60 overflow-y-auto rounded-md border border-gray-200 bg-white">
        {filtered.length === 0 ? (
          <div className="p-4 text-center text-sm text-gray-500">
            {candidates.length === 0
              ? 'Žádní kandidáti — všichni už mají oprávnění nebo jsou admini.'
              : 'Filtru neodpovídá žádný uživatel.'}
          </div>
        ) : (
          <ul>
            {filtered.map((c) => {
              const checked = selected.has(c.id);
              return (
                <li
                  key={c.id}
                  className={
                    checked
                      ? 'flex cursor-pointer items-center gap-3 border-b border-gray-100 bg-teal-light/40 px-3 py-2 last:border-0'
                      : 'flex cursor-pointer items-center gap-3 border-b border-gray-100 px-3 py-2 last:border-0 hover:bg-gray-50'
                  }
                  onClick={() => toggle(c.id)}
                >
                  <input type="checkbox" checked={checked} onChange={() => toggle(c.id)} />
                  <div className="flex-1 text-sm">
                    <div className="font-semibold text-dark">{c.name}</div>
                    <div className="text-xs text-gray-500">{c.email}</div>
                  </div>
                  <span
                    className={
                      c.role === 'reader'
                        ? 'rounded-md bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-600'
                        : 'rounded-md bg-teal/15 px-2 py-0.5 text-xs font-semibold text-teal-dark'
                    }
                  >
                    {c.role}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Permissions to grant + submit */}
      <div className="flex flex-wrap items-end gap-4">
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={canRead} onChange={(e) => setCanRead(e.target.checked)} />
          Číst
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={canEdit}
            onChange={(e) => {
              setCanEdit(e.target.checked);
              if (e.target.checked) setCanRead(true);
            }}
          />
          Editovat
        </label>
        {canEdit && selectedReaderCount > 0 && (
          <span className="text-xs text-gold">
            {selectedReaderCount}× Reader ve výběru — edit-právo se pro ně nepřidělí (nutno povýšit
            roli zvlášť).
          </span>
        )}
        <div className="ml-auto flex gap-2">
          <button type="button" className="btn-ghost" onClick={onCancel} disabled={isPending}>
            Zrušit
          </button>
          <button
            className="btn-primary"
            disabled={selectedArr.length === 0 || isPending}
            onClick={() =>
              onSubmit({
                user_ids: selectedArr,
                can_read: canRead,
                can_edit: canEdit,
                reader_ids: readerIds,
              })
            }
          >
            {isPending
              ? `Přidávám ${selectedArr.length}…`
              : `Přidat ${selectedArr.length} ${plural(selectedArr.length)}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function plural(n: number): string {
  if (n === 1) return 'uživatele';
  if (n >= 2 && n <= 4) return 'uživatele';
  return 'uživatelů';
}

function Toggle({
  value,
  onChange,
  disabled,
  title,
}: {
  value: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  title?: string;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      title={title}
      onClick={() => onChange(!value)}
      className={
        value
          ? 'rounded-md bg-positive-bg px-2.5 py-1 text-xs font-semibold text-positive hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-50'
          : 'rounded-md bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-400 hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-50'
      }
    >
      {value ? 'Ano' : 'Ne'}
    </button>
  );
}
